if (!customElements.get('podcast-card-grid')) {
  customElements.define(
    'podcast-card-grid',
    class PodcastCardGrid extends HTMLElement {
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-podcast-card-item]'));
        this.itemsContainer = this.querySelector('.product-card-grid__items');
        this.tabs = Array.from(this.querySelectorAll('[data-season-filter]'));
        this.loadMoreButton = this.querySelector('[data-podcast-load-more]');
        this.emptyMessage = this.querySelector('[data-podcast-empty]');
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.activeSeason = 'all';
        this.visibleSeasonCount = this.getInitialCount();
        this.seasonOrder = this.collectSeasonOrder();
        this.originalCardIndexes = new Map(this.cards.map((card, index) => [card, index]));

        this.sortCards();

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.setSeason(tab.dataset.seasonFilter || 'all'));
        });

        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', () => {
            this.visibleSeasonCount += this.getIncrementCount();
            this.render();
          });
        }

        this.mediaQuery.addEventListener('change', () => this.render());
        this.render();
      }

      setSeason(season) {
        this.activeSeason = season;
        this.visibleSeasonCount = this.getInitialCount();

        this.tabs.forEach((tab) => {
          const isActive = (tab.dataset.seasonFilter || 'all') === season;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.render();
      }

      render() {
        const visibleCards = this.getVisibleCards();

        this.syncCardStyles(visibleCards);

        this.cards.forEach((card) => {
          card.hidden = true;
        });

        visibleCards.forEach((card) => {
          card.hidden = false;
        });

        if (this.emptyMessage) {
          this.emptyMessage.hidden = visibleCards.length > 0;
        }

        if (this.loadMoreButton) {
          this.loadMoreButton.hidden =
            this.activeSeason !== 'all' || this.visibleSeasonCount >= this.getSeasonOrder().length;
        }
      }

      getVisibleCards() {
        if (this.activeSeason !== 'all') {
          return this.cards.filter((card) => card.dataset.season === this.activeSeason);
        }

        const visibleSeasons = this.getSeasonOrder().slice(0, this.visibleSeasonCount);

        return this.cards.filter((card) => visibleSeasons.includes(card.dataset.season));
      }

      getSeasonOrder() {
        return this.seasonOrder;
      }

      collectSeasonOrder() {
        return this.cards.reduce((seasons, card) => {
          const season = card.dataset.season;

          if (season && !seasons.includes(season)) {
            seasons.push(season);
          }

          return seasons;
        }, []);
      }

      sortCards() {
        const seasonIndexes = new Map(this.seasonOrder.map((season, index) => [season, index]));
        const sortedCards = [...this.cards].sort((firstCard, secondCard) => {
          const firstSeasonIndex = seasonIndexes.has(firstCard.dataset.season)
            ? seasonIndexes.get(firstCard.dataset.season)
            : Number.MAX_SAFE_INTEGER;
          const secondSeasonIndex = seasonIndexes.has(secondCard.dataset.season)
            ? seasonIndexes.get(secondCard.dataset.season)
            : Number.MAX_SAFE_INTEGER;

          if (firstSeasonIndex !== secondSeasonIndex) {
            return firstSeasonIndex - secondSeasonIndex;
          }

          const episodeNumberDifference =
            this.getEpisodeNumber(secondCard) - this.getEpisodeNumber(firstCard);

          if (episodeNumberDifference !== 0) {
            return episodeNumberDifference;
          }

          return this.originalCardIndexes.get(firstCard) - this.originalCardIndexes.get(secondCard);
        });

        const automaticCards = sortedCards.filter((card) => this.getFeaturedPriority(card) === 0);
        const firstFeaturedCard = sortedCards.find((card) => this.getFeaturedPriority(card) === 1);
        const secondFeaturedCard = sortedCards.find((card) => this.getFeaturedPriority(card) === 2);
        const firstCard = firstFeaturedCard || automaticCards.shift();
        const secondCard = secondFeaturedCard || automaticCards.shift();
        const frontCards = [firstCard, secondCard].filter(Boolean);

        this.cards = [...frontCards, ...sortedCards.filter((card) => !frontCards.includes(card))];

        if (this.itemsContainer) {
          this.cards.forEach((card) => this.itemsContainer.append(card));
        }
      }

      getFeaturedPriority(card) {
        const priority = Number.parseInt(card.dataset.featuredPriority || '0', 10);

        return Number.isNaN(priority) || priority < 1 ? 0 : priority;
      }

      getEpisodeNumber(card) {
        const episodeNumber = Number.parseFloat(card.dataset.episodeNumber || '');

        return Number.isNaN(episodeNumber) ? Number.MIN_SAFE_INTEGER : episodeNumber;
      }

      syncCardStyles(cards) {
        cards.forEach((item, index) => {
          const isLarge = index < 2;
          const card = item.querySelector('.ls-product-card');

          item.classList.toggle('product-card-grid__item--large', isLarge);
          item.classList.toggle('product-card-grid__item--regular', !isLarge);

          if (card) {
            card.classList.toggle('ls-product-card--large', isLarge);
            card.classList.toggle('ls-product-card--regular', !isLarge);
          }
        });
      }

      getInitialCount() {
        const parsed = Number.parseInt(this.dataset.initialCount || '2', 10);

        return Number.isNaN(parsed) ? 2 : parsed;
      }

      getIncrementCount() {
        const parsed = Number.parseInt(this.dataset.incrementCount || '1', 10);

        return Number.isNaN(parsed) ? 1 : parsed;
      }
    }
  );
}
